import { useMemo } from 'react';
import { differenceInMinutes, addHours, format } from 'date-fns';

/**
 * Builds timeline geometry for the RecipeDisplay chart area.
 *
 * Window definition:
 * - starts 3.5 hours before `currentTime`
 * - spans 12 hours total
 *
 * @param {Array<Object>} productionRuns - Live production runs (kept for recalculation dependency).
 * @param {Date} [currentTime=new Date()] - Reference time for computing the visible window.
 * @returns {{
 *   minDate: Date,
 *   maxDate: Date,
 *   totalDurationMinutes: number,
 *   timeMarkers: Array<{time: Date, label: string, leftPercent: number}>,
 *   hourSlots: Array<{
 *     index: number,
 *     time: Date,
 *     label: string,
 *     leftPercent: number,
 *     widthPercent: number,
 *     rightPercent: number,
 *     startDate: Date,
 *     endDate: Date
 *   }>,
 *   firstMarkerTime: Date
 * }}
 */
export function useTimeline(productionRuns, currentTime = new Date()) {
  return useMemo(() => {
    const HOURS_PER_DAY = 12;

    // Start timeline exactly 3.5 hours before current time (not rounded).
    const startTime = addHours(currentTime, -3.5);
    const maxDate = addHours(startTime, HOURS_PER_DAY);
    const totalDurationMinutes = differenceInMinutes(maxDate, startTime);

    // Hourly markers aligned to :00, shifted by the same 3.5h offset.
    const timeMarkers = [];
    const firstMarkerTime = new Date(currentTime);
    firstMarkerTime.setMinutes(0, 0, 0);
    const firstMarker = addHours(firstMarkerTime, -3.5);

    let currentMarkerTime = new Date(firstMarker);
    while (currentMarkerTime <= maxDate) {
      const leftPercent = (differenceInMinutes(currentMarkerTime, startTime) / totalDurationMinutes) * 100;
      timeMarkers.push({
        time: new Date(currentMarkerTime),
        label: format(currentMarkerTime, 'HH:mm'),
        leftPercent,
      });
      currentMarkerTime = addHours(currentMarkerTime, 1);
    }

    // Build background slot segments between markers for grid rendering.
    const hourSlots = [];
    for (let i = 0; i < timeMarkers.length; i++) {
      const marker = timeMarkers[i];
      const nextMarker = timeMarkers[i + 1];

      const slotLeftPercent = marker.leftPercent;
      const slotRightPercent = nextMarker ? nextMarker.leftPercent : 100;
      const slotWidthPercent = slotRightPercent - slotLeftPercent;

      hourSlots.push({
        index: i,
        time: new Date(marker.time),
        label: marker.label,
        leftPercent: slotLeftPercent,
        widthPercent: slotWidthPercent,
        rightPercent: slotRightPercent,
        startDate: new Date(marker.time),
        endDate: nextMarker ? new Date(nextMarker.time) : maxDate,
      });
    }

    return {
      minDate: startTime,
      maxDate,
      totalDurationMinutes,
      timeMarkers,
      hourSlots,
      firstMarkerTime: firstMarker,
    };
    // Recompute on production run updates to keep marker-relative UI in sync.
  }, [productionRuns, currentTime]);
}
